import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { CheckCircle2, Home as HomeIcon, CalendarPlus } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { API } from "@/lib/api";

export default function PaymentSuccess() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const bookingIdParam = params.get("booking_id");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let active = true;
    const loadBooking = async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/${bookingIdParam}`);
        if (active) setBooking(data);
      } catch { /* ignore */ }
    };
    if (bookingIdParam) loadBooking();
    return () => { active = false; };
  }, [bookingIdParam]);

  const fmtFull = (iso) => iso && new Date(iso).toLocaleString(i18n.language, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

  return (
    <section className="bg-warm-ivory" data-testid="payment-success-page">
      <div className="mx-auto max-w-2xl px-5 py-24 text-center lg:py-32">
        <Reveal>
            <CheckCircle2 className="mx-auto h-14 w-14 text-champagne-gold" />
            <h1 className="mt-6 font-serif text-4xl text-deep-ink lg:text-5xl">{t("booking.success.title")}</h1>
            <p className="mt-4 text-lg leading-relaxed text-deep-ink/70">{t("booking.success.text")}</p>
            <p className="mt-2 text-sm text-champagne-gold">{t("booking.success.reserved")}</p>

            {booking && (
              <div className="mx-auto mt-10 max-w-md rounded-3xl bg-sand p-7 text-start">
                <p className="text-xs uppercase tracking-editorial text-deep-ink/50">{t("booking.success.reference")}</p>
                <p className="font-serif text-2xl text-deep-ink" data-testid="booking-reference">{booking.reference}</p>
                <div className="mt-4 space-y-1 text-sm text-deep-ink/70">
                  <p>{t(`booking.types.${booking.type_id}.name`)}</p>
                  <p>{fmtFull(booking.slot)}</p>
                  <p>{t(`booking.formats.${booking.format}`)}</p>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {booking && (
                <a href={`${API}/bookings/${booking.id}/ics`} data-testid="download-ics"
                  className="inline-flex items-center gap-2 rounded-full border border-champagne-gold px-6 py-3 text-deep-ink transition-colors hover:bg-champagne-gold/15">
                  <CalendarPlus className="h-4 w-4" />{t("booking.success.addToCalendar", "Add to calendar")}
                </a>
              )}
              <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-deep-ink/20 px-6 py-3 text-deep-ink transition-colors hover:border-champagne-gold hover:text-champagne-gold">
                <HomeIcon className="h-4 w-4" />{t("booking.success.home")}
              </Link>
              <Link to="/book" className="inline-flex items-center gap-2 rounded-full bg-deep-ink px-6 py-3 text-warm-ivory transition-colors hover:bg-champagne-gold hover:text-deep-ink">
                <CalendarPlus className="h-4 w-4" />{t("booking.success.bookAnother")}
              </Link>
            </div>
        </Reveal>
      </div>
    </section>
  );
}
